import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Modal,
  InputGroup,
} from "react-bootstrap";
import {
  FaSearch,
  FaUserSlash,
  FaPlus,
  FaEdit,
  FaFileExcel,
  FaBan,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import * as XLSX from "xlsx";
import {
  requestApprovalWithReasonModal,
  showApprovalRequestCreatedAlert,
} from "../../functions/approvalFlowAlerts";

const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, "us-central1");
const BLACKLIST_PROCESS_TYPE = "blacklist_removal";

const ListaNegra = ({ usuario }) => {
  const [registros, setRegistros] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removalRequests, setRemovalRequests] = useState([]);
  const [processingRequestId, setProcessingRequestId] = useState("");

  const [formData, setFormData] = useState({
    dni: "",
    nombres: "",
    apellidos: "",
    motivo: "",
    fecha: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    cargarRegistros();
    cargarUsuariosDisponibles();
    cargarSolicitudesRetiro();
    ensureBlacklistPolicy();
  }, [usuario.ruc]);

  const cargarRegistros = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "pl_lista_negra"),
        where("ruc_empresa", "==", usuario.ruc)
      );
      const querySnapshot = await getDocs(q);
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
      });
      setRegistros(docs.filter((item) => String(item.estado || "active") !== "removed"));
    } catch (error) {
      console.error("Error al cargar lista negra:", error);
      swal.fire("Error", "No se pudieron cargar los registros", "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarSolicitudesRetiro = async () => {
    try {
      const q = query(
        collection(db, `governance/${usuario.ruc}/approvalRequests`),
        where("processType", "==", BLACKLIST_PROCESS_TYPE)
      );
      const snap = await getDocs(q);
      const docs = [];
      snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
      setRemovalRequests(docs);
    } catch (error) {
      console.error("Error al cargar solicitudes de retiro:", error);
      setRemovalRequests([]);
    }
  };

  const ensureBlacklistPolicy = async () => {
    if (!usuario?.ruc) return;
    if (!["admin", "superAdmin"].includes(String(usuario?.rol || ""))) return;
    try {
      const callable = httpsCallable(functions, "upsertTenantApprovalPolicy");
      await callable({
        tenantId: usuario.ruc,
        processType: BLACKLIST_PROCESS_TYPE,
        policy: {
          levels: [
            { level: 1, roles: ["rh"], minApprovers: 1 },
            { level: 2, roles: ["admin"], minApprovers: 1 },
          ],
          constraints: {
            separationOfDuties: true,
            sameApproverAcrossLevels: false,
          },
        },
      });
    } catch (error) {
      console.error("Error configurando política de aprobación de lista negra:", error);
    }
  };

  const cargarUsuariosDisponibles = async () => {
    try {
      const q = query(
        collection(db, "users"),
        where("rucEmpresa", "==", usuario.ruc)
      );
      const querySnapshot = await getDocs(q);
      const docs = [];
      querySnapshot.forEach((item) => {
        const data = item.data() || {};
        if (data.dni && data.name && data.apellidos) {
          docs.push({
            id: item.id,
            dni: String(data.dni).trim(),
            nombres: String(data.name).trim().toUpperCase(),
            apellidos: String(data.apellidos).trim().toUpperCase(),
            email: String(data.email || "").trim().toLowerCase(),
          });
        }
      });
      docs.sort((a, b) =>
        `${a.apellidos} ${a.nombres}`.localeCompare(`${b.apellidos} ${b.nombres}`)
      );
      setUsuariosDisponibles(docs);
    } catch (error) {
      console.error("Error al cargar usuarios para lista negra:", error);
      setUsuariosDisponibles([]);
    }
  };

  const handleOpenModal = (registro = null) => {
    if (registro) {
      setEditMode(true);
      setCurrentId(registro.id);
      setFormData({
        dni: registro.dni,
        nombres: registro.nombres,
        apellidos: registro.apellidos,
        motivo: registro.motivo,
        fecha: registro.fecha || new Date().toISOString().split("T")[0],
      });
    } else {
      setEditMode(false);
      setCurrentId(null);
      setFormData({
        dni: "",
        nombres: "",
        apellidos: "",
        motivo: "",
        fecha: new Date().toISOString().split("T")[0],
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.dni || !formData.nombres || !formData.apellidos || !formData.motivo) {
      swal.fire("Atención", "Por favor completa todos los campos obligatorios", "warning");
      return;
    }

    if (!editMode) {
      const yaExiste = registros.some(
        (r) => String(r.dni || "").trim().toUpperCase() === String(formData.dni || "").trim().toUpperCase()
      );
      if (yaExiste) {
        swal.fire("Atención", "Este usuario ya se encuentra en la lista negra.", "warning");
        return;
      }
    }

    setSaving(true);
    try {
      const usuarioSeleccionado = usuariosDisponibles.find(
        (u) => String(u.dni || "").trim().toUpperCase() === String(formData.dni || "").trim().toUpperCase()
      );
      const data = {
        ...formData,
        dni: formData.dni.trim().toUpperCase(),
        nombres: formData.nombres.trim().toUpperCase(),
        apellidos: formData.apellidos.trim().toUpperCase(),
        email: String(usuarioSeleccionado?.email || "").trim().toLowerCase(),
        ruc_empresa: usuario.ruc,
        estado: "active",
        bloqueo_total: true,
        registrado_por: usuario.email,
        updated_at: serverTimestamp(),
      };

      if (editMode) {
        const docRef = doc(db, "pl_lista_negra", currentId);
        await updateDoc(docRef, data);
        swal.fire("Éxito", "Registro actualizado correctamente", "success");
      } else {
        data.created_at = serverTimestamp();
        const added = await addDoc(collection(db, "pl_lista_negra"), data);

        // Marcar al usuario en lista negra para excluirlo de listas operativas
        const userRef = doc(db, "users", data.dni);
        const batch = writeBatch(db);
        batch.set(
          userRef,
          {
            blacklistActive: true,
            blacklistEntryId: added.id,
            activo: false,
            updated_at: serverTimestamp(),
          },
          { merge: true }
        );
        await batch.commit();
        swal.fire("Éxito", "Registro añadido a la lista negra", "success");
      }
      handleCloseModal();
      cargarRegistros();
      cargarSolicitudesRetiro();
    } catch (error) {
      console.error("Error al guardar:", error);
      swal.fire("Error", "Ocurrió un problema al guardar el registro", "error");
    } finally {
      setSaving(false);
    }
  };

  const getRequestByBlacklistId = (blacklistId) =>
    removalRequests.find(
      (r) =>
        String(r.resourceId || "") === String(blacklistId || "") &&
        /^pending_l\d+$/i.test(String(r.status || ""))
    );

  const getRequestLevels = (request) => {
    const rawLevels = Array.isArray(request?.levels) ? request.levels : [];
    const normalized = rawLevels
      .map((lvl, index) => {
        const level = Number(lvl?.level || index + 1);
        const roles = Array.isArray(lvl?.roles)
          ? lvl.roles.map((role) => String(role || "").trim()).filter(Boolean)
          : [];
        const role = String(roles[0] || "").trim();
        return {
          level,
          roles,
          role: role || `nivel_${level}`,
        };
      })
      .filter((lvl) => Number.isFinite(lvl.level) && lvl.level > 0)
      .sort((a, b) => a.level - b.level);
    if (normalized.length > 0) return normalized;
    return [
      { level: 1, roles: ["rh"], role: "rh" },
      { level: 2, roles: ["admin"], role: "admin" },
    ];
  };

  const toRoleLabel = (role) => {
    const value = String(role || "").trim().toLowerCase();
    if (!value) return "Rol";
    if (value === "rh") return "RH";
    if (value === "admin") return "Admin";
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const getPendingLevelNumber = (request) => {
    const match = String(request?.status || "").match(/^pending_l(\d+)$/i);
    return match ? Number(match[1]) : null;
  };

  const handleRequestRemoval = async (registro) => {
    const currentPending = getRequestByBlacklistId(registro.id);
    const requestLevels = getRequestLevels(currentPending);
    const modalLevels = requestLevels.map((lvl) => ({
      role: toRoleLabel(lvl.role),
      level: `L${lvl.level}`,
    }));

    const result = await requestApprovalWithReasonModal({
      title: "Solicitar salida de lista negra",
      actionLabel: "la salida de lista negra",
      inputLabel: "Motivo de solicitud (flujo de aprobación obligatorio)",
      inputPlaceholder: "Explique por qué corresponde retirar al trabajador...",
      confirmButtonText: "Enviar solicitud",
      levels: modalLevels,
    });

    if (!result.isConfirmed) return;

    try {
      setProcessingRequestId(registro.id);
      const callable = httpsCallable(functions, "submitApprovalRequest");
      await callable({
        tenantId: usuario.ruc,
        processType: BLACKLIST_PROCESS_TYPE,
        resourceType: "pl_lista_negra",
        resourceId: registro.id,
        reason: result.value,
        payloadSnapshot: {
          dni: registro.dni,
          nombres: registro.nombres,
          apellidos: registro.apellidos,
          email: registro.email || "",
        },
      });
      await showApprovalRequestCreatedAlert({
        pendingSubject: "La solicitud de salida de lista negra",
        levels: modalLevels.map((lvl) => `${lvl.role} (${lvl.level})`),
      });
      await cargarSolicitudesRetiro();
      await cargarRegistros();
    } catch (error) {
      console.error("Error al solicitar retiro:", error);
      await swal.fire("Error", error?.message || "No se pudo registrar la solicitud.", "error");
    } finally {
      setProcessingRequestId("");
    }
  };

  const handleApprove = async (requestId, decision) => {
    const result = await swal.fire({
      title: decision === "approve" ? "Aprobar solicitud" : "Rechazar solicitud",
      input: "textarea",
      inputLabel: "Comentario",
      inputPlaceholder: "Comentario opcional...",
      showCancelButton: true,
      confirmButtonText: decision === "approve" ? "Confirmar aprobación" : "Confirmar rechazo",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    try {
      setProcessingRequestId(requestId);
      const callable = httpsCallable(functions, "decideApprovalRequest");
      const response = await callable({
        tenantId: usuario.ruc,
        requestId,
        decision,
        comment: String(result.value || "").trim(),
      });
      const status = String(response?.data?.status || "");
      const levelMatch = status.match(/^pending_l(\d+)$/i);
      const msg =
        levelMatch
          ? `Aprobación registrada. Pendiente aprobación de Nivel ${levelMatch[1]}.`
          : status === "approved"
            ? "Solicitud aprobada."
            : status === "rejected"
              ? "Solicitud rechazada."
              : "Operación completada.";
      await swal.fire("Éxito", msg, "success");
      await cargarSolicitudesRetiro();
      await cargarRegistros();
    } catch (error) {
      console.error("Error al procesar aprobación:", error);
      await swal.fire("Error", error?.message || "No se pudo procesar la solicitud.", "error");
    } finally {
      setProcessingRequestId("");
    }
  };

  const exportToExcel = () => {
    const dataToExport = registros.map((r) => ({
      DNI: r.dni,
      Nombres: r.nombres,
      Apellidos: r.apellidos,
      Motivo: r.motivo,
      Fecha: r.fecha,
      "Registrado por": r.registrado_por,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lista Negra");
    XLSX.writeFile(wb, `Lista_Negra_${usuario.ruc}.xlsx`);
  };

  const filteredRegistros = registros.filter(
    (r) =>
      r.dni.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.nombres.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.apellidos.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectUsuario = (e) => {
    const dniSeleccionado = e.target.value;
    const usuarioSeleccionado = usuariosDisponibles.find(
      (u) => String(u.dni) === String(dniSeleccionado)
    );
    if (!usuarioSeleccionado) {
      setFormData((prev) => ({ ...prev, dni: "", nombres: "", apellidos: "" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      dni: usuarioSeleccionado.dni,
      nombres: usuarioSeleccionado.nombres,
      apellidos: usuarioSeleccionado.apellidos,
    }));
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaBan className="me-3" />
                Lista Negra de Empleados
              </h2>
              <p className="mb-0 opacity-75">
                Gestión y control de trabajadores con restricción de recontratación
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1} className="text-center">
                    <div
                      className="rounded-3 p-3 d-inline-block"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                      }}
                    >
                      <FaUserSlash size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">
                      Registro de Incidencias Graves
                    </h5>
                    <p className="text-muted mb-0 small">
                      Administra la lista de personal no elegible para nuevas contrataciones debido a comportamientos o incidencias previas.
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <Button
                      onClick={() => handleOpenModal()}
                      className="rounded-pill px-4 me-2 border-0 shadow-sm"
                      style={{
                        background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      }}
                    >
                      <FaPlus className="me-2" /> Añadir Registro
                    </Button>
                    <Button
                      variant="outline-success"
                      className="rounded-pill px-4 border-2"
                      onClick={exportToExcel}
                      disabled={registros.length === 0}
                    >
                      <FaFileExcel className="me-2" /> Exportar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas rápidas */}
            <Row className="mb-4">
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaBan size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{registros.length}</h4>
                    <p className="mb-0 small opacity-75">Total Lista Negra</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaUserSlash size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">
                      {registros.filter(r => {
                        const date = new Date(r.fecha);
                        const now = new Date();
                        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                      }).length}
                    </h4>
                    <p className="mb-0 small opacity-75">Registros del Mes</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaExclamationTriangle size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">
                      {registros.filter(r => r.fecha === new Date().toISOString().split("T")[0]).length}
                    </h4>
                    <p className="mb-0 small opacity-75">Registros de Hoy</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaSearch size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">
                      {searchTerm ? filteredRegistros.length : registros.length}
                    </h4>
                    <p className="mb-0 small opacity-75">Resultados Filtro</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Barra de búsqueda */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-3">
                <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                  <InputGroup.Text 
                    className="border-0 px-4"
                    style={{ 
                      background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      color: "white",
                    }}
                  >
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    className="border-0 py-3"
                    placeholder="Buscar por DNI, Nombres o Apellidos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ backgroundColor: "white" }}
                  />
                </InputGroup>
              </Card.Body>
            </Card>

            {/* Tabla Principal */}
            <Card 
              className="border-0 shadow-sm"
              style={{ borderRadius: "15px", overflow: "hidden" }}
            >
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Cargando lista negra...</p>
                  </div>
                ) : filteredRegistros.length > 0 ? (
                  <Table
                    hover
                    className="align-middle bg-white mb-0"
                    style={{ fontSize: "0.95rem" }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #dee2e6"
                      }}
                    >
                      <tr>
                        <th className="py-3 px-4 text-secondary fw-semibold">DNI</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Nombre Completo</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Motivo</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Fecha Reporte</th>
                        <th className="py-3 px-4 text-secondary fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRegistros.map((reg) => (
                        <tr key={reg.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                          {(() => {
                            const pendingRequest = getRequestByBlacklistId(reg.id);
                            const pendingLevel = getPendingLevelNumber(pendingRequest);
                            const requestLevels = getRequestLevels(pendingRequest);
                            const currentLevelConfig = requestLevels.find(
                              (lvl) => lvl.level === pendingLevel
                            );
                            const currentLevelRoles = Array.isArray(currentLevelConfig?.roles)
                              ? currentLevelConfig.roles
                              : [currentLevelConfig?.role];
                            const canApproveCurrentLevel =
                              Boolean(pendingRequest) &&
                              Number.isFinite(pendingLevel) &&
                              currentLevelRoles
                                .map((role) => String(role || "").trim().toLowerCase())
                                .includes(String(usuario?.rol || "").trim().toLowerCase());
                            return (
                              <>
                          <td className="px-4 py-3 fw-bold text-primary">{reg.dni}</td>
                          <td className="px-4 py-3">{`${reg.nombres} ${reg.apellidos}`}</td>
                          <td className="px-4 py-3">
                            <div
                              style={{
                                maxWidth: "400px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={reg.motivo}
                            >
                              {reg.motivo}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge 
                              bg="info" 
                              className="px-3 py-2"
                              style={{ 
                                borderRadius: "8px", 
                                background: "rgba(59, 130, 246, 0.1)",
                                color: "#1e40af",
                                border: "1px solid rgba(59, 130, 246, 0.2)"
                              }}
                            >
                              {reg.fecha}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="link"
                              className="text-primary p-0 me-3"
                              onClick={() => handleOpenModal(reg)}
                            >
                              <FaEdit size={18} />
                            </Button>
                            {!pendingRequest && (
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="me-2"
                                onClick={() => handleRequestRemoval(reg)}
                                disabled={processingRequestId === reg.id}
                              >
                                Solicitar salida
                              </Button>
                            )}
                            {pendingRequest && (
                              <Badge bg={pendingLevel === 1 ? "warning" : "info"} className="me-2">
                                {Number.isFinite(pendingLevel)
                                  ? `Esperando ${toRoleLabel(currentLevelConfig?.role)} (L${pendingLevel})`
                                  : "Pendiente de aprobación"}
                              </Badge>
                            )}
                            {canApproveCurrentLevel && (
                              <>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  className="me-1"
                                  disabled={processingRequestId === pendingRequest.id}
                                  onClick={() => handleApprove(pendingRequest.id, "approve")}
                                >
                                  <FaCheck />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={processingRequestId === pendingRequest.id}
                                  onClick={() => handleApprove(pendingRequest.id, "reject")}
                                >
                                  <FaTimes />
                                </Button>
                              </>
                            )}
                          </td>
                              </>
                            );
                          })()}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <FaBan size="4rem" className="text-muted opacity-25 mb-3" />
                    <h5 className="text-muted fw-semibold">
                      {searchTerm ? "No se encontraron resultados" : "No hay registros en la lista negra"}
                    </h5>
                    <p className="text-muted">
                      {searchTerm ? "Intenta con otro término de búsqueda" : "Los empleados reportados aparecerán aquí."}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal - Estilo Premium coincidente */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        size="lg" 
        centered
        contentClassName="border-0 shadow-lg"
        style={{ borderRadius: "15px" }}
      >
        <Modal.Header 
          closeButton 
          className="text-white border-0"
          style={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
            borderRadius: "15px 15px 0 0"
          }}
        >
          <Modal.Title className="fw-bold">
            {editMode ? <><FaEdit className="me-2" /> Editar Registro</> : <><FaPlus className="me-2" /> Añadir a Lista Negra</>}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <Row>
              {!editMode && (
                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Seleccionar Usuario *</Form.Label>
                    <Form.Select
                      className="border-0 shadow-sm py-2"
                      style={{ borderRadius: "10px" }}
                      value={formData.dni}
                      onChange={handleSelectUsuario}
                      required
                    >
                      <option value="">-- Seleccione un usuario --</option>
                      {usuariosDisponibles.map((u) => (
                        <option key={u.id} value={u.dni}>
                          {u.dni} - {u.apellidos} {u.nombres}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">DNI *</Form.Label>
                  <Form.Control
                    name="dni"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.dni}
                    onChange={handleChange}
                    placeholder="8 dígitos"
                    readOnly={!editMode}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Nombres *</Form.Label>
                  <Form.Control
                    name="nombres"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.nombres}
                    onChange={handleChange}
                    placeholder="Nombres"
                    readOnly={!editMode}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Apellidos *</Form.Label>
                  <Form.Control
                    name="apellidos"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.apellidos}
                    onChange={handleChange}
                    placeholder="Apellidos"
                    readOnly={!editMode}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Motivo del Reporte *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="motivo"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px", resize: "none" }}
                    value={formData.motivo}
                    onChange={handleChange}
                    placeholder="Describe detalladamente el motivo por el cual no se recomienda su recontratación..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Fecha del Reporte</Form.Label>
                  <Form.Control
                    type="date"
                    name="fecha"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.fecha}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light p-3" style={{ borderRadius: "0 0 15px 15px" }}>
            <Button 
              variant="light" 
              className="rounded-pill px-4 fw-semibold text-muted" 
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="rounded-pill px-4 fw-bold border-0 shadow-sm"
              style={{
                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              }}
              disabled={saving}
            >
              {saving ? "Procesando..." : editMode ? "Guardar Cambios" : "Registrar en Lista"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ListaNegra;
